
import VCountdown from 'vue-awesome-countdown/src/vue-awesome-countdown.vue'
import { mapState } from 'vuex'
import { queueStatus } from '~/enums/queueStatus'
import sharedMessages from '~/locales/common.translate'
import messages from '~/locales/feedback.translate'
import { getErrorMessage } from '~/utils/errors'

export default {
	components: {
		VCountdown,
	},

	computed: {
		...mapState(['scToken']),
		...mapState('user', ['userId', 'clan']),
	},

	i18n: {
		messages,
		sharedMessages,
	},

	props: {
		showModal: {
			type: Boolean,
		},
		challengeId: {
			type: String,
		},
		matchSocket: {
			type: Object,
		},
	},

	watch: {
		showModal(newShowModal) {
			if (newShowModal) {
				this.resetModalState()
				this.endDate = Date.now() + this.TIMEOUT_WINDOW * 1000
			}
		},
	},

	data: () => ({
		TIMEOUT_WINDOW: 15, // seconds
		endDate: null,
		matchAcceptedByBoth: false,
		matchAccepted: false,
		matchDeclined: false,
		actionsDisabled: false,
		attemptingToGetMatch: 0,
	}),

	mounted() {
		this.matchSocket?.on('update', (data) => {
			if (data?.event === 'matches:match_up_declined') {
				this.onMatchupDeclined()
			} else if (data?.event === 'matches:match_up_accepted') {
				this.matchAcceptedByBoth = true
				// https://github.com/mlinquan/vue-awesome-countdown#methods
				if (this.$refs && this.$refs.timer) {
					this.$refs.timer.stopCountdown()
				}
				setTimeout(() => {
					this.checkMatchReady(data?.data?.matchSeries?.id)
				}, 2000)
			}
		})
	},

	methods: {
		resetModalState() {
			this.matchAccepted = false
			this.actionsDisabled = false
			this.matchAcceptedByBoth = false
			this.matchDeclined = false
			this.endDate = null
			this.attemptingToGetMatch = 0
		},

		onCountdownEnd() {
			if (!this.matchAccepted) {
				this.$store.commit('user/setQueueStatus', queueStatus.NONE)
				this.$emit('close')
			} else {
				setTimeout(() => {
					this.matchDeclined = true
					setTimeout(() => {
						if (!this.matchAcceptedByBoth) {
							this.resetModalState()
							this.$emit('close')
						}
					}, 3000)
				}, 2000)
			}
		},

		onMatchupDeclined() {
			this.matchDeclined = true
			this.actionsDisabled = true
			// https://github.com/mlinquan/vue-awesome-countdown#methods
			if (this.$refs && this.$refs.timer) {
				this.$refs.timer.stopCountdown()
			}
			setTimeout(() => {
				this.resetModalState()
				this.$emit('close')
			}, 3000)
		},

		async onAccept() {
			this.actionsDisabled = true
			const promises = [
				this.$warApi.getClanWarStatus(this.clan.tag),
				this.$scApi.checkSelectedPlayers(this.scToken),
			]

			const [clanWarData, clanStatusData] = await Promise.allSettled(promises)

			if (clanStatusData?.status !== 'fulfilled') {
				this.actionsDisabled = false
				this.$store.commit('user/setQueueStatus', queueStatus.NONE)
				this.resetModalState()
				this.$emit('close')

				const errorPath = getErrorMessage(clanStatusData?.reason)
				this.$router.push(this.localePath(errorPath))
				return
			}

			if (clanWarData?.value?.data?.clanInWar !== false) {
				this.actionsDisabled = false
				this.$store.commit('user/setQueueStatus', queueStatus.NONE)
				this.resetModalState()
				this.$emit('close')
				this.$router.push(this.localePath('/error/23'))
				return
			}

			try {
				await this.$api.matches.matchSeries.acceptMatchup(this.challengeId)
				this.matchAccepted = true
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(e)

				this.$store.commit('user/setQueueStatus', queueStatus.NONE)
				this.resetModalState()
				this.$emit('close')
				this.$router.push(this.localePath('/error'))
			}
		},

		async onDecline() {
			try {
				this.actionsDisabled = true
				if (this.$refs && this.$refs.timer) {
					this.$refs.timer.stopCountdown()
				}
				await this.$api.matches.matchSeries.declineMatchup(this.challengeId)
				this.matchDeclined = true
				this.$store.commit('user/setQueueStatus', queueStatus.NONE)
				this.resetModalState()
				this.$emit('close')
			} catch (e) {
				setTimeout(() => {
					this.$store.commit('user/setQueueStatus', queueStatus.NONE)
					this.resetModalState()
					this.$emit('close')
				}, 3000)
			}
		},

		async checkMatchReady(matchSeriesId) {
			try {
				const warData = await this.$warApi.getWarDeeplink(matchSeriesId, this.scToken)

				this.$logApi.createLogMessage(
					`match-found-modal executing deeplink for match ${matchSeriesId}`
				)
				const deeplink = warData.data.link

				if (deeplink) {
					document.location = deeplink

					this.$store.commit('user/setQueueStatus', queueStatus.NONE)

					setTimeout(() => {
						this.$logApi.createLogMessage(
							`match-found-modal executing OpenWar for match ${matchSeriesId}`
						)
						window.location = 'clashofclans://OpenWar'
					}, 1000)

					setTimeout(() => {
						this.resetModalState()
						this.$emit('close')
					}, 2000)
				} else {
					this.$logApi.createLogMessage(
						`match-found-modal did not get deeplink for match ${matchSeriesId}`
					)
					setTimeout(() => {
						this.resetModalState()
						this.$emit('close')
					}, 2000)
				}
			} catch (err) {
				this.$logApi.createLogMessage(
					`match-found-modal failed to execute deeplink for match ${matchSeriesId}`,
					{
						error: JSON.stringify(err),
					}
				)
				setTimeout(() => {
					this.checkMatchReady(matchSeriesId)
				}, 2000)
			}
		},
	},
}
